import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { formatNumber } from '@tempo-io/fim3-formulas';
import { Input, InputWithIcon } from '@tempo-io/sui-input';
import type { ChangeEvent, WheelEvent as ReactWheelEvent } from 'react';
import { useTranslation } from 'react-i18next';

const getCurrencySymbol = (currency: string | undefined | null) => {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    default:
      return '';
  }
};

export const NumberFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation('translation');

  if (
    fieldConfiguration.fieldType !== 'NUMBER' &&
    fieldConfiguration.fieldType !== 'CURRENCY' &&
    fieldConfiguration.fieldType !== 'PERCENT'
  )
    return null;

  let currencySymbol: string | null = null;
  if (fieldConfiguration.fieldType === 'CURRENCY') {
    currencySymbol = getCurrencySymbol(fieldConfiguration.currency);
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValues([event.target.value]);
  };

  const onBlur = () => {
    let value = values[0];
    if (value) {
      if (fieldConfiguration.fieldType === 'CURRENCY') {
        value = parseFloat(value).toFixed(2);
      }

      if (fieldConfiguration.places !== null && fieldConfiguration.places !== undefined) {
        value = formatNumber(value, fieldConfiguration.places);
      }
    }
    setValues(value ? [value].filter(Boolean) : []);
  };

  const numberInputOnWheelPreventChange = (e: ReactWheelEvent<HTMLInputElement>) => {
    if (document.activeElement !== e.target) return;

    e.preventDefault();
    e.stopPropagation();

    (e.target as HTMLInputElement).blur();

    setTimeout(() => {
      (e.target as HTMLInputElement).focus();
    }, 0);
  };

  const sharedProps = {
    className: 'fs-exclude',
    'data-dd-action-name': 'FIM 3 Custom Field - number field redacted',
    'data-testid': 'fim3-custom-field-input-number-input-field',
    title: '',
    value: values[0] ?? '',
    onChange,
    onBlur,
    onWheel: numberInputOnWheelPreventChange,
    step: fieldConfiguration.increment ?? undefined,
    min: fieldConfiguration.minimum ?? undefined,
    max: fieldConfiguration.maximum ?? undefined,
    type: 'number' as const,
    placeholder: isEditableText ? undefined : t('ideaManagement.general.numericFieldPlaceholder'),
    ...rest,
  };

  if (currencySymbol) {
    return <InputWithIcon leftIcon={<span>{currencySymbol}</span>} {...sharedProps} />;
  }

  return <Input {...sharedProps} />;
};
